import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Illustrations de l'installation obligatoire (29/09/2026) — reproduction dessinée de l'écran
 * réel du téléphone à chaque étape, l'élément à toucher entouré en bleu, pour que personne ne soit
 * perdu avec les seuls mots. Fidèles aux planches validées OnboardingIOSEtape1..3 et
 * OnboardingAndroidEtape1..3 (design/, canevas « Refonte Rise-T »).
 *
 * Volontairement en styles en ligne et en couleurs fixes : ce sont des images du système du
 * téléphone (Safari, Chrome, écran d'accueil), pas des éléments de l'interface Rise-T — elles ne
 * suivent donc ni le thème clair/sombre ni les jetons de couleur de l'application.
 */

const SVG = ({ d, taille = 22, couleur = "#0E1726", extra }) => (
  <svg
    width={taille}
    height={taille}
    viewBox="0 0 24 24"
    fill="none"
    stroke={couleur}
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {d && <path d={d} />}
    {extra}
  </svg>
);

const CHEMINS = {
  partager: "M12 15V3M8 7l4-4 4 4M6 11H5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-1",
  retour: "M15 5l-7 7 7 7",
  avant: "M9 5l7 7-7 7",
  livre: "M4 5a2 2 0 0 1 2-2h12v17H6a2 2 0 0 0-2 2V5zM4 20V5",
  onglets: "M5 8v11a2 2 0 0 0 2 2h11",
  plus: "M12 8v8M8 12h8",
  copier: "M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2",
  etoile: "M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z",
  installer: "M12 8v6M9.5 11.5L12 14l2.5-2.5",
  ajouter: "M12 8v8M8 12h8",
  cadenas: "M8 11V8a4 4 0 0 1 8 0v3",
};

const ANNEAU = {
  outline: "3px solid #0180D0",
  outlineOffset: "2px",
  boxShadow: "0 0 0 7px rgba(1,128,208,.22)",
  borderRadius: "12px",
};

const Etiquette = ({ children, style }) => (
  <span
    style={{
      position: "absolute",
      background: "#0180D0",
      color: "#fff",
      fontSize: "11.5px",
      fontWeight: 700,
      padding: "3px 9px",
      borderRadius: "999px",
      whiteSpace: "nowrap",
      boxShadow: "0 2px 6px rgba(0,0,0,.25)",
      zIndex: 5,
      ...style,
    }}
  >
    {children}
  </span>
);

const Cadre = ({ hauteur, children }) => (
  <div
    aria-hidden="true"
    style={{
      position: "relative",
      width: "100%",
      height: `${hauteur}px`,
      borderRadius: "18px",
      overflow: "hidden",
      background: "#fff",
      border: "1px solid var(--border-2)",
      boxShadow: "var(--sh-2)",
      fontFamily: "-apple-system,'Segoe UI',Roboto,'Public Sans',sans-serif",
      color: "#0E1726",
    }}
  >
    {children}
  </div>
);

const BarreAdresse = ({ rond }) => (
  <div
    style={{
      height: "38px",
      flexGrow: 1,
      borderRadius: rond ? "19px" : "12px",
      background: "#E5E9EF",
      display: "flex",
      alignItems: "center",
      justifyContent: rond ? "flex-start" : "center",
      gap: "6px",
      padding: rond ? "0 14px" : 0,
      fontSize: "13px",
      color: "#4A5768",
    }}
  >
    <SVG d={CHEMINS.cadenas} taille={12} couleur="#4A5768" extra={<rect x="5" y="11" width="14" height="9" rx="2" />} />
    rise-together.site
  </div>
);

const PageRise = ({ t }) => (
  <span style={{ color: "#8494A8", fontSize: "12px" }}>{t("onboardingEcran.illustrations.page")}</span>
);

/** iPhone, étape 1 — la barre du bas de Safari, icône Partager entourée. */
const IosBarre = ({ t }) => (
  <Cadre hauteur={230}>
    <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: "78px", background: "#F4F6FA", padding: "10px 14px" }}>
      <BarreAdresse />
    </div>
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: "78px",
        bottom: "64px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PageRise t={t} />
    </div>
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "64px",
        background: "#F4F6FA",
        borderTop: "1px solid #DCE3EC",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        padding: "0 6px",
      }}
    >
      <SVG d={CHEMINS.retour} taille={24} couleur="#8494A8" />
      <SVG d={CHEMINS.avant} taille={24} couleur="#8494A8" />
      <span style={{ display: "flex", padding: "8px", background: "#fff", ...ANNEAU }}>
        <SVG d={CHEMINS.partager} taille={26} couleur="#0180D0" />
      </span>
      <SVG d={CHEMINS.livre} taille={24} couleur="#4A5768" />
      <SVG taille={24} couleur="#4A5768" extra={<><rect x="8" y="3" width="13" height="13" rx="2" /><path d={CHEMINS.onglets} /></>} />
    </div>
    <Etiquette style={{ left: "50%", bottom: "78px", transform: "translateX(-50%)" }}>
      {t("onboardingEcran.illustrations.toucherIci")}
    </Etiquette>
  </Cadre>
);

const LigneFeuille = ({ libelle, icone, actif }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: "46px",
      padding: "0 14px",
      background: "#fff",
      borderBottom: actif ? "none" : "1px solid #EEF2F7",
      ...(actif ? { ...ANNEAU, position: "relative", zIndex: 2 } : {}),
    }}
  >
    <span style={{ fontSize: "13.5px", fontWeight: actif ? 700 : 400 }}>{libelle}</span>
    {icone(actif ? "#0180D0" : "#4A5768")}
  </div>
);

/** iPhone, étape 2 — la feuille de partage, « Sur l'écran d'accueil » entouré. */
const IosFeuille = ({ t }) => (
  <Cadre hauteur={250}>
    <div style={{ position: "absolute", inset: 0, background: "rgba(14,23,38,.35)" }} />
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        background: "#EEF2F7",
        borderRadius: "16px 16px 0 0",
        padding: "10px 12px 14px",
      }}
    >
      <div style={{ width: "36px", height: "4px", borderRadius: "9px", background: "#C4CFDD", margin: "0 auto 10px" }} />
      <div style={{ borderRadius: "12px", background: "#fff" }}>
        <LigneFeuille
          libelle={t("onboardingEcran.illustrations.copier")}
          icone={(c) => <SVG taille={20} couleur={c} extra={<><rect x="8" y="8" width="12" height="12" rx="2" /><path d={CHEMINS.copier} /></>} />}
        />
        <LigneFeuille libelle={t("onboardingEcran.illustrations.favoris")} icone={(c) => <SVG d={CHEMINS.etoile} taille={20} couleur={c} />} />
        <LigneFeuille
          actif
          libelle={t("onboardingEcran.illustrations.ecranAccueil")}
          icone={(c) => <SVG taille={20} couleur={c} extra={<><rect x="3" y="3" width="18" height="18" rx="4" /><path d={CHEMINS.plus} /></>} />}
        />
        <LigneFeuille libelle={t("onboardingEcran.illustrations.listeLecture")} icone={(c) => <SVG d={CHEMINS.livre} taille={20} couleur={c} />} />
      </div>
    </div>
    <Etiquette style={{ right: "14px", top: "14px" }}>{t("onboardingEcran.illustrations.defilerIci")}</Etiquette>
  </Cadre>
);

/** Android, étape 1 — la barre d'adresse de Chrome, menu ⋮ entouré. */
const AndroidBarre = ({ t }) => (
  <Cadre hauteur={170}>
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        height: "56px",
        background: "#F4F6FA",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "0 12px",
      }}
    >
      <BarreAdresse rond />
      <span style={{ display: "flex", padding: "7px", background: "#fff", ...ANNEAU }}>
        <SVG
          taille={22}
          extra={
            <>
              <circle cx="12" cy="5" r="1.6" fill="#0E1726" />
              <circle cx="12" cy="12" r="1.6" fill="#0E1726" />
              <circle cx="12" cy="19" r="1.6" fill="#0E1726" />
            </>
          }
        />
      </span>
    </div>
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: "56px",
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PageRise t={t} />
    </div>
    <Etiquette style={{ right: "12px", top: "78px" }}>{t("onboardingEcran.illustrations.toucherIci")}</Etiquette>
  </Cadre>
);

const LigneMenu = ({ libelle, icone, actif }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "14px",
      height: "44px",
      padding: "0 16px",
      background: actif ? "#E3F1FB" : "#fff",
      ...(actif ? { ...ANNEAU, position: "relative", zIndex: 2 } : {}),
    }}
  >
    {icone(actif ? "#0180D0" : "#4A5768")}
    <span style={{ fontSize: "13.5px", fontWeight: actif ? 700 : 400 }}>{libelle}</span>
  </div>
);

/** Android, étape 2 — le menu de Chrome, « Installer l'application » entouré. */
const AndroidMenu = ({ t }) => (
  <Cadre hauteur={226}>
    <div
      style={{
        position: "absolute",
        right: "12px",
        top: "12px",
        left: "60px",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 8px 28px rgba(14,23,38,.28)",
        border: "1px solid #DCE3EC",
      }}
    >
      <LigneMenu libelle={t("onboardingEcran.illustrations.favori")} icone={(c) => <SVG d={CHEMINS.etoile} taille={20} couleur={c} />} />
      <LigneMenu
        actif
        libelle={t("onboardingEcran.illustrations.installer")}
        icone={(c) => <SVG taille={20} couleur={c} extra={<><rect x="6" y="2" width="12" height="20" rx="2.5" /><path d={CHEMINS.installer} /></>} />}
      />
      <LigneMenu
        libelle={t("onboardingEcran.illustrations.ajouterAccueil")}
        icone={(c) => <SVG taille={20} couleur={c} extra={<><rect x="4" y="3" width="16" height="18" rx="3" /><path d={CHEMINS.ajouter} /></>} />}
      />
      <LigneMenu
        libelle={t("onboardingEcran.illustrations.partager")}
        icone={(c) => <SVG taille={20} couleur={c} extra={<><rect x="8" y="8" width="12" height="12" rx="2" /><path d={CHEMINS.copier} /></>} />}
      />
    </div>
    <Etiquette style={{ left: "14px", bottom: "12px" }}>{t("onboardingEcran.illustrations.lUneOuLautre")}</Etiquette>
  </Cadre>
);

const Appli = ({ couleur, nom }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", width: "64px" }}>
    <span style={{ width: "50px", height: "50px", borderRadius: "13px", background: couleur }} />
    <span style={{ fontSize: "10.5px", color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,.5)" }}>{nom}</span>
  </div>
);

/** Étape 3 (commune) — l'écran d'accueil du téléphone, icône Rise-T entourée. */
const EcranAccueil = ({ t }) => (
  <Cadre hauteur={230}>
    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg,#0180D0 0%,#6E56CF 55%,#D2095F 100%)" }} />
    <div style={{ position: "absolute", left: 0, right: 0, top: "22px", display: "flex", justifyContent: "space-around" }}>
      <Appli couleur="#0E9F6E" nom="Messages" />
      <Appli couleur="#F3901A" nom="Photos" />
      <Appli couleur="#0E1726" nom={t("onboardingEcran.illustrations.horloge")} />
      <Appli couleur="#8494A8" nom={t("onboardingEcran.illustrations.reglages")} />
    </div>
    <div style={{ position: "absolute", left: 0, right: 0, top: "104px", display: "flex", justifyContent: "space-around" }}>
      <Appli couleur="#fff" nom={t("onboardingEcran.illustrations.agenda")} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", width: "64px", position: "relative", zIndex: 2 }}>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "50px",
            height: "50px",
            borderRadius: "13px",
            background: "#fff",
            ...ANNEAU,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/rise-logo.png`}
            alt="Rise-T"
            width="38"
            height="38"
            style={{ width: "38px", height: "38px", objectFit: "contain" }}
          />
        </span>
        <span style={{ fontSize: "10.5px", color: "#fff", fontWeight: 700, textShadow: "0 1px 2px rgba(0,0,0,.5)" }}>Rise-T</span>
      </div>
      <Appli couleur="#3AD29D" nom="Notes" />
      <Appli couleur="#F5407F" nom={t("onboardingEcran.illustrations.musique")} />
    </div>
    <Etiquette style={{ left: "50%", top: "190px", transform: "translateX(-50%)" }}>
      {t("onboardingEcran.illustrations.ouvrirIci")}
    </Etiquette>
  </Cadre>
);

/**
 * `plateforme` : "ios" (Safari) ou "android" (Chrome) ; `etape` : 1, 2 ou 3 — la dernière est
 * la même pour les deux.
 */
export default function IllustrationInstallation({ plateforme, etape }) {
  const { t } = useTranslation();
  if (etape === 3) return <EcranAccueil t={t} />;
  if (plateforme === "ios") return etape === 1 ? <IosBarre t={t} /> : <IosFeuille t={t} />;
  return etape === 1 ? <AndroidBarre t={t} /> : <AndroidMenu t={t} />;
}
